// store/slices/siteSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import CustomerService from '@/services/customers';
import { setSelectedCustomer } from './selectionSlice';

export interface Site {
  site_id: string | null;
  id: string;
  name: string;
  site_name?: string;
  gdm_customer_number?: string;
  customerId: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  postal_code?: string;
  default?: boolean;
  world_area?: string;
  // Add other site properties as needed
}

interface SiteState {
  data: Site | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  selectedCustomerId: string | null; // Track which customer these sites belong to
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  initialLoad: boolean;
  storeCode: string | null;
  currentPage: number;
  totalCount: number;
  hasMore: boolean;
}

const initialState: SiteState = {
  data: null,
  loading: false,
  loadingMore: false,
  error: null,
  selectedCustomerId: null,
  fetched: false,
  status: 'idle',
  initialLoad: false,
  storeCode: null,
  currentPage: 1,
  totalCount: 0,
  hasMore: false,
};

// Async thunk for fetching sites based on customer
export const fetchSites = createAsyncThunk<
    SiteState,
  { userId: string; type: string, id: string, accessToken?: string, loadDefault: boolean, page?: number, limit?: number, search?: string, country?: string, province?: string, city?: string,pagination?: string }
>(
  'site/fetchSites',
    async ({userId, type, id, accessToken, loadDefault, page = 1, limit = 100, search, country, province, city, pagination}) => {

        const customerService = new CustomerService(undefined, accessToken);
        const apiResponse = await customerService.getSites(userId, type, id, loadDefault, page, limit, search, country, province, city, pagination);
        console.log('fetchSites: fetched sites', apiResponse, 'search:', search);
        if(!loadDefault){
          apiResponse.sites?.forEach((element: any) => {
            delete element.default;
          });
        }

        return apiResponse;
  }
);

// Async thunk for loading more sites (pagination)
export const fetchMoreSites = createAsyncThunk<
    SiteState,
  { userId: string; type: string, id: string, accessToken?: string, page: number, limit?: number, search?: string, country?: string, province?: string, city?: string, pagination?: string }
>(
  'site/fetchMoreSites',
    async ({userId, type, id, accessToken, page, limit = 100, search, country, province, city, pagination}) => {

        const customerService = new CustomerService(undefined, accessToken);
        const apiResponse = await customerService.getSites(userId, type, id, false, page, limit, search, country, province, city, pagination);
        console.log('fetchMoreSites: fetched more sites', apiResponse, 'search:', search);

        return apiResponse;
  }
);

const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    clearSites: (state) => {
      state.data = null;
      state.error = null;
      state.selectedCustomerId = null;
      state.fetched = false;
      state.status = 'idle';
      state.currentPage = 1;
      state.totalCount = 0;
      state.hasMore = false;
      state.loadingMore = false;
    },
    setStoreCode: (state, action: PayloadAction<string>) => {
      state.storeCode = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSites.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fetched = false;
        state.status = 'loading';
      })
      .addCase(fetchSites.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.loading = false;
        state.data = payload as Site;
        state.fetched = true;
        state.status = 'succeeded';
        state.initialLoad = false;
        state.currentPage = 1;
        state.totalCount = payload?.total || 0;
        const sitesArray = Array.isArray(payload) ? payload : (payload?.sites || []);
        state.hasMore = state.totalCount > sitesArray.length;
        
        console.log('Initial fetch - Sites count:', sitesArray.length, 'Total:', state.totalCount, 'Has more:', state.hasMore);
      })
      .addCase(fetchSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch sites';
        state.fetched = true;
        state.status = 'failed';
        state.initialLoad = false;
      })
      .addCase(setSelectedCustomer, (state) => {
        state.data = null;   // <-- IMPORTANT
        state.loading = false;
        state.status = 'idle';
        state.currentPage = 1;
        state.totalCount = 0;
        state.hasMore = false;
        state.loadingMore = false;
      })
      .addCase(fetchMoreSites.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(fetchMoreSites.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.loadingMore = false;
        
        // Append new sites to existing data
        const existingSites = Array.isArray(state.data) ? state.data : (state.data as any)?.sites || [];
        const newSites = Array.isArray(payload) ? payload : (payload?.sites || []);
        
        console.log('Appending sites - Existing:', existingSites.length, 'New:', newSites.length);
        
        // Always maintain the object structure with sites array
        if (state.data && typeof state.data === 'object' && !Array.isArray(state.data)) {
          // Preserve the existing structure and append to sites array
          (state.data as any).sites = [...existingSites, ...newSites];
          // Update metadata from payload if available
          if (payload?.total !== undefined) (state.data as any).total = payload.total;
        } else {
          // Create the proper structure if it doesn't exist
          state.data = {
            ...payload,
            sites: [...existingSites, ...newSites]
          } as any;
        }
        
        state.currentPage = action.meta.arg.page;
        state.totalCount = payload?.total || state.totalCount;
        const allSitesCount = existingSites.length + newSites.length;
        state.hasMore = state.totalCount > allSitesCount;        
      })
      .addCase(fetchMoreSites.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.error.message || 'Failed to fetch more sites';
      });
  },
});



export const { clearSites,setStoreCode } = siteSlice.actions;
export default siteSlice.reducer;